import {
  BdArrayUtil,
  OverrideProperty,
  OverridePropertyHelper,
} from '@bindoc-projects/util-domain-lib';
import type {
  FilterConditionPluginSpecification,
} from '../filter.plugin';
import {
  FilterConditionPluginImpl
} from '../filter.plugin';
import type { FilterConditionPlugin } from '../filter';
import type {
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  BdContentCondition,
} from '@bindoc-projects/condition-domain-lib';
import { AYS_FABS_EXTENDED } from '@bindoc-projects/clinic-domain-lib';
import type { FilterDetailedText } from '../filter-detailed-text';

export const BD_FAB_KEY_CONTENT_CONDITION_ID = 'fabKey';
export const FilterConditionFabKeysPluginType: FilterConditionPluginSpecification<
  string[],
  FilterConditionFabKeysDto,
  BdFabKeyContentCondition
> = {
  type: 'fabKeys',
  category: 'case',
  displayName: { de: '§301 Fachabteilungsschlüssel' },
  sortingIndex: 8,
  occurrence: 'single',
  fromObject(
    obj: FilterConditionFabKeysDto,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionFabKeysPlugin(obj.fabKeys);
  },
  fromCondition(
    jsonObj: BdFabKeyContentCondition,
  ): FilterConditionPluginImpl<string[]> {
    return new FilterConditionFabKeysPlugin(jsonObj.includes ?? []);
  },
  newInstance(): FilterConditionPluginImpl<string[]> {
    return new FilterConditionFabKeysPlugin([]);
  },
};

export class FilterConditionFabKeysPlugin extends FilterConditionPluginImpl<
  string[],
  FilterConditionFabKeysDto
> {
  static override Specification: FilterConditionPluginSpecification<
    string[],
    FilterConditionFabKeysDto,
    BdFabKeyContentCondition
  > = FilterConditionFabKeysPluginType;

  constructor(private fabKeys: string[]) {
    super(FilterConditionFabKeysPluginType.type);
  }

  clone(): FilterConditionFabKeysPlugin {
    return new FilterConditionFabKeysPlugin([...this.fabKeys]);
  }

  override equals(other: FilterConditionFabKeysPlugin): boolean {
    return (
      super.equals(other) && BdArrayUtil.equals(this.fabKeys, other.fabKeys)
    );
  }

  override overrideClone(
    override: OverrideProperty[],
  ): FilterConditionPlugin<string[]> {
    const fabKeyClone: string[] = this.fabKeys
      ? OverridePropertyHelper.overrideProperties(
          this.fabKeys,
          'fabKey',
          override,
          (value) => value,
        )
      : null;
    return new FilterConditionFabKeysPlugin(fabKeyClone);
  }

  override getOverrideProperties(
    tileId: string,
    dependencyVirtualClinicId: string,
  ): OverrideProperty[] {
    return this.fabKeys.map((fabKey) => {
      return new OverrideProperty<string>(
        tileId,
        'fabKey',
        fabKey,
        fabKey,
        'possible',
        dependencyVirtualClinicId,
      );
    });
  }

  override validationToText(): string {
    if (this.fabKeys.length === 0) {
      return 'Mindestens einen §301 Fachabteilungsschlüssel auswählen';
    } else {
      return '';
    }
  }

  toCondition(): BdCondition | null {
    return new BdFabKeyContentCondition(this.fabKeys, []);
  }

  override toDetailedText(): FilterDetailedText | null {
    const title = this.toText();
    if (!title) return null;
    const items = [...this.fabKeys]
      .sort((a, b) => a.localeCompare(b, 'de', { numeric: true }))
      .map((key) => {
        const fab = AYS_FABS_EXTENDED.find((f) => f.fabKey === key);
        return fab ? `${key} - ${fab.name}` : key;
      });
    return { title, items };
  }

  toText(): string | null {
    return this.fabKeys.length === 1
      ? 'einen §301 Fachabteilungsschlüssel'
      : `${this.fabKeys.length} §301 Fachabteilungsschlüssel`;
  }

  toJSON(): FilterConditionFabKeysDto | null {
    return {
      type: this.type,
      fabKeys: this.fabKeys,
    };
  }

  toValue(): string[] {
    return this.fabKeys;
  }
}

export class BdFabKeyContentCondition extends BdContentCondition<string> {
  constructor(includes: string[], excludes: string[]) {
    super(BD_FAB_KEY_CONTENT_CONDITION_ID, includes, excludes);
  }
}

interface FilterConditionFabKeysDto {
  type: string;
  fabKeys: string[];
}
